import { Schema, z } from 'zod';
import { BaseFieldDefinition, BaseFieldDefinitionSchema } from './base';

export namespace StringFieldDefinition {
  export const TYPE = 'string';
}

/**
 * Field definition of type string
 * @typedef StringFieldDefinition
 * @property - type : type of the field.
 * @property - defaultValue : default value if value isn't set.
 * @property - i18n : translatable properties of the field.
 * @property - validations : an optional object for defining rules about how a module definition can be instantiated.
 */
export type StringFieldDefinition = BaseFieldDefinition & {
  type: typeof StringFieldDefinition.TYPE;
  defaultValue?: string;
  i18n?: boolean;
  validations?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
  };
};

export const StringFieldDefinitionSchema = BaseFieldDefinitionSchema.and(
  z.object({
    type: z.literal(StringFieldDefinition.TYPE),
    defaultValue: z.string().optional(),
    i18n: z.boolean().optional(),
    validations: z
      .object({
        minLength: z.number().optional(),
        maxLength: z.number().optional(),
        pattern: z.string().optional()
      })
      .optional()
  })
);

export namespace BooleanFieldDefinition {
  export const TYPE = 'boolean';
}

/**
 * Field definition of type boolean
 * @typedef BooleanFieldDefinition
 * @property - type : type of the field.
 * @property - defaultValue : default value if value isn't set.
 */
export type BooleanFieldDefinition = BaseFieldDefinition & {
  type: typeof BooleanFieldDefinition.TYPE;
  defaultValue?: boolean;
};

export const BooleanFieldDefinitionSchema: Schema<BooleanFieldDefinition> =
  BaseFieldDefinitionSchema.and(
    z.object({
      type: z.literal(BooleanFieldDefinition.TYPE),
      defaultValue: z.boolean().optional()
    })
  );

export namespace NumberFieldDefinition {
  export const TYPE = 'number';
}

/**
 * Field definition of type number
 * @typedef NumberFieldDefinition
 * @property - type : type of the field.
 * @property - defaultValue : default value if value isn't set.
 * @property - validations : an optional object for defining rules about how a module definition can be instantiated.
 */
export type NumberFieldDefinition = BaseFieldDefinition & {
  type: typeof NumberFieldDefinition.TYPE;
  defaultValue?: number;
  validations?: {
    isInteger?: boolean;
    min?: number;
    max?: number;
  };
};

export const NumberFieldDefinitionSchema: Schema<NumberFieldDefinition> =
  BaseFieldDefinitionSchema.and(
    z.object({
      type: z.literal(NumberFieldDefinition.TYPE),
      defaultValue: z.number().optional(),
      validations: z
        .object({
          isInteger: z.boolean().optional(),
          min: z.number().optional(),
          max: z.number().optional()
        })
        .optional()
    })
  );

/**
 * Primitive type Field definition (string, number, boolean)
 * @typedef PrimitiveFieldDefinition
 */
export type PrimitiveFieldDefinition =
  | StringFieldDefinition
  | BooleanFieldDefinition
  | NumberFieldDefinition;

export const PrimitiveFieldDefinitionSchema = StringFieldDefinitionSchema.or(
  BooleanFieldDefinitionSchema
).or(NumberFieldDefinitionSchema);
